import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdAdd, MdCloudDone, MdDelete, MdDevices, MdEdit, MdRefresh, MdWarningAmber } from 'react-icons/md';
import { toast } from 'react-toastify';
import PageContainer from '../../../../shared/layout/containers/PageContainer';
import PageHeader from '../../../../shared/layout/headers/PageHeader';
import PageTitle from '../../../../shared/layout/titles/PageTitle';
import PageContent from '../../../../shared/layout/containers/PageContent';
import LoadingMessage from '../../../../shared/layout/feedback/LoadingMessage';
import ErrorMessage from '../../../../shared/layout/feedback/ErrorMessage';
import StatsGrid from '../../../../shared/layout/containers/StatsGrid';
import StatCard from '../../../../shared/layout/cards/StatCard';
import {
    fetchDistributorIntegrations,
    fetchDistributorIntegration,
    createDistributorIntegration,
    updateDistributorIntegration,
    deleteDistributorIntegration,
    rotateDistributorIntegrationCredentials,
    pingDistributorIntegrationWebhook,
} from '../../../../services/Http/DistributorApi';

const defaultForm = {
    id: null,
    name: '',
    integration_type: 'custom',
    email: '',
    webhook_url: '',
    config: {
        endpoint: '',
        sync_interval: 'daily',
    },
    is_active: true,
};

const DistributorIntegrations = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [connectors, setConnectors] = useState([]);
    const [stats, setStats] = useState({ total: 0, active: 0, webhook_alerts: 0, types: {} });
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [form, setForm] = useState(defaultForm);
    const [saving, setSaving] = useState(false);
    const [lastSecret, setLastSecret] = useState(null);

    const loadIntegrations = useCallback(async () => {
        setLoading(true);
        setError(false);
        try {
            const response = await fetchDistributorIntegrations();
            const rows = response.data || [];
            setConnectors(rows);
            setStats(response.stats || { total: 0, active: 0, webhook_alerts: 0, types: {} });
            setSelectedId((prev) => {
                if (prev && rows.some((row) => row.id === prev)) {
                    return prev;
                }
                return rows[0]?.id || null;
            });
        } catch (err) {
            console.error(err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadIntegrations();
    }, [loadIntegrations]);

    const loadDetail = useCallback(async (integrationId) => {
        if (!integrationId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        try {
            const response = await fetchDistributorIntegration(integrationId);
            setDetail(response);
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorIntegrations.toast.error'));
        } finally {
            setDetailLoading(false);
        }
    }, [t]);

    useEffect(() => {
        loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    const openModal = (connector = null) => {
        if (connector) {
            setForm({
                id: connector.id,
                name: connector.name,
                integration_type: connector.integration_type,
                email: connector.email || '',
                webhook_url: connector.webhook_url || '',
                config: {
                    endpoint: connector.config?.endpoint || '',
                    sync_interval: connector.config?.sync_interval || 'daily',
                },
                is_active: connector.is_active,
            });
        } else {
            setForm({ ...defaultForm, config: { ...defaultForm.config } });
        }
        setModalOpen(true);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            if (form.id) {
                await updateDistributorIntegration(form.id, form);
                toast.success(t('DistributorIntegrations.toast.updated'));
            } else {
                const response = await createDistributorIntegration(form);
                if (response?.api_secret) {
                    setLastSecret(response.api_secret);
                    toast.info(t('DistributorIntegrations.toast.secret'));
                }
                toast.success(t('DistributorIntegrations.toast.created'));
            }
            setModalOpen(false);
            setForm({ ...defaultForm, config: { ...defaultForm.config } });
            await loadIntegrations();
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorIntegrations.toast.error'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (connector) => {
        if (!window.confirm(t('DistributorIntegrations.actions.confirmDelete'))) {
            return;
        }
        try {
            await deleteDistributorIntegration(connector.id);
            toast.success(t('DistributorIntegrations.toast.deleted'));
            if (connector.id === selectedId) {
                setSelectedId(null);
            }
            await loadIntegrations();
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorIntegrations.toast.error'));
        }
    };

    const handleRotate = async (connector) => {
        try {
            const response = await rotateDistributorIntegrationCredentials(connector.id);
            setLastSecret(response.api_secret);
            toast.success(t('DistributorIntegrations.toast.token'));
            await loadIntegrations();
            await loadDetail(connector.id);
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorIntegrations.toast.error'));
        }
    };

    const handleWebhookTest = async (connector) => {
        try {
            await pingDistributorIntegrationWebhook(connector.id);
            toast.success(t('DistributorIntegrations.toast.webhook'));
            await loadIntegrations();
            await loadDetail(connector.id);
        } catch (err) {
            console.error(err);
            toast.error(t('DistributorIntegrations.toast.error'));
        }
    };

    const typeBreakdown = useMemo(() => {
        const entries = Object.entries(stats.types || {});
        if (!entries.length) {
            return null;
        }
        return entries.map(([type, count]) => `${type}: ${count}`).join(' · ');
    }, [stats.types]);

    if (loading) {
        return <LoadingMessage>{t('DistributorIntegrations.loading')}</LoadingMessage>;
    }

    if (error) {
        return <ErrorMessage>{t('DistributorIntegrations.error')}</ErrorMessage>;
    }

    return (
        <PageContainer>
            <PageHeader>
                <div>
                    <PageTitle>{t('DistributorIntegrations.title')}</PageTitle>
                    <Subtitle>{t('DistributorIntegrations.subtitle')}</Subtitle>
                </div>
                <PrimaryButton onClick={() => openModal()}>
                    <MdAdd /> {t('DistributorIntegrations.actions.add')}
                </PrimaryButton>
            </PageHeader>
            <PageContent>
                <StatsGrid>
                    <StatCard title={t('DistributorIntegrations.stats.total')} value={stats.total} icon={<MdDevices />} color="users" />
                    <StatCard title={t('DistributorIntegrations.stats.active')} value={stats.active} icon={<MdCloudDone />} color="success" />
                    <StatCard title={t('DistributorIntegrations.stats.webhook')} value={stats.webhook_alerts} icon={<MdWarningAmber />} color="warning" />
                </StatsGrid>
                {typeBreakdown && <TypeBreakdown>{typeBreakdown}</TypeBreakdown>}
                <Layout>
                    <TableCard>
                        <Table>
                            <thead>
                                <tr>
                                    <th>{t('DistributorIntegrations.table.name')}</th>
                                    <th>{t('DistributorIntegrations.table.type')}</th>
                                    <th>{t('DistributorIntegrations.table.status')}</th>
                                    <th>{t('DistributorIntegrations.table.lastSync')}</th>
                                    <th>{t('DistributorIntegrations.table.webhook')}</th>
                                    <th>{t('DistributorIntegrations.table.actions')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {connectors.map((connector) => (
                                    <tr key={connector.id} onClick={() => setSelectedId(connector.id)} className={connector.id === selectedId ? 'active' : ''}>
                                        <td>{connector.name}</td>
                                        <td>{connector.integration_type || t('DistributorIntegrations.form.custom')}</td>
                                        <td>
                                            <StatusBadge $status={connector.status}>{t(`DistributorIntegrations.status.${connector.status}`)}</StatusBadge>
                                        </td>
                                        <td>{connector.last_sync_at ? new Date(connector.last_sync_at).toLocaleString() : t('DistributorIntegrations.table.never')}</td>
                                        <td>
                                            <WebhookBadge $status={connector.webhook_health}>
                                                {t(`DistributorIntegrations.webhook.${connector.webhook_health}`)}
                                            </WebhookBadge>
                                        </td>
                                        <td>
                                            <IconButton type="button" onClick={(event) => { event.stopPropagation(); openModal(connector); }}>
                                                <MdEdit />
                                            </IconButton>
                                            <IconButton type="button" onClick={(event) => { event.stopPropagation(); handleRotate(connector); }}>
                                                <MdRefresh />
                                            </IconButton>
                                            <IconButton type="button" onClick={(event) => { event.stopPropagation(); handleWebhookTest(connector); }}>
                                                <MdWarningAmber />
                                            </IconButton>
                                            <IconButton type="button" onClick={(event) => { event.stopPropagation(); handleDelete(connector); }}>
                                                <MdDelete />
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableCard>
                    <DetailCard>
                        {detailLoading && <LoadingMessage>{t('DistributorIntegrations.loading')}</LoadingMessage>}
                        {!detailLoading && detail && (
                            <DetailContent>
                                <h4>{detail.name}</h4>
                                <DetailList>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.type')}</strong>
                                        <span>{detail.integration_type}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.endpoint')}</strong>
                                        <span>{detail.config?.endpoint || '—'}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.sync')}</strong>
                                        <span>{detail.config?.sync_interval || '—'}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.apiKey')}</strong>
                                        <span>{detail.api_key || '—'}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.apiSecret')}</strong>
                                        <span>{lastSecret || '—'}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.webhookUrl')}</strong>
                                        <span>{detail.webhook_url || '—'}</span>
                                    </li>
                                    <li>
                                        <strong>{t('DistributorIntegrations.detail.webhookSecret')}</strong>
                                        <span>{detail.webhook_secret || '—'}</span>
                                    </li>
                                </DetailList>
                            </DetailContent>
                        )}
                        {!detailLoading && !detail && <Placeholder>{t('DistributorIntegrations.empty')}</Placeholder>}
                    </DetailCard>
                </Layout>
            </PageContent>

            {modalOpen && (
                <ModalOverlay>
                    <ModalContent>
                        <ModalHeader>
                            <h3>{form.id ? t('DistributorIntegrations.editTitle') : t('DistributorIntegrations.addTitle')}</h3>
                            <button type="button" onClick={() => setModalOpen(false)}>
                                ×
                            </button>
                        </ModalHeader>
                        <form onSubmit={handleSubmit}>
                            <label>{t('DistributorIntegrations.form.name')}</label>
                            <input
                                type="text"
                                value={form.name}
                                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                            />
                            <label>{t('DistributorIntegrations.form.type')}</label>
                            <input
                                type="text"
                                value={form.integration_type}
                                onChange={(event) => setForm((prev) => ({ ...prev, integration_type: event.target.value }))}
                            />
                            <label>{t('DistributorIntegrations.form.email')}</label>
                            <input
                                type="email"
                                value={form.email}
                                onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
                            />
                            <label>{t('DistributorIntegrations.form.endpoint')}</label>
                            <input
                                type="url"
                                value={form.config.endpoint}
                                onChange={(event) => setForm((prev) => ({ ...prev, config: { ...prev.config, endpoint: event.target.value } }))}
                            />
                            <label>{t('DistributorIntegrations.form.sync')}</label>
                            <select
                                value={form.config.sync_interval}
                                onChange={(event) => setForm((prev) => ({ ...prev, config: { ...prev.config, sync_interval: event.target.value } }))}
                            >
                                <option value="realtime">{t('DistributorIntegrations.form.syncRealtime')}</option>
                                <option value="hourly">{t('DistributorIntegrations.form.syncHourly')}</option>
                                <option value="daily">{t('DistributorIntegrations.form.syncDaily')}</option>
                            </select>
                            <label>{t('DistributorIntegrations.form.webhook')}</label>
                            <input
                                type="url"
                                value={form.webhook_url}
                                onChange={(event) => setForm((prev) => ({ ...prev, webhook_url: event.target.value }))}
                            />
                            <CheckboxRow>
                                <input
                                    type="checkbox"
                                    checked={form.is_active}
                                    onChange={(event) => setForm((prev) => ({ ...prev, is_active: event.target.checked }))}
                                />
                                <span>{t('DistributorIntegrations.form.isActive')}</span>
                            </CheckboxRow>
                            <ModalActions>
                                <SecondaryButton type="button" onClick={() => setModalOpen(false)}>
                                    {t('DistributorIntegrations.actions.cancel')}
                                </SecondaryButton>
                                <PrimaryButton type="submit" disabled={saving}>
                                    {saving ? t('DistributorIntegrations.actions.saving') : t('DistributorIntegrations.actions.save')}
                                </PrimaryButton>
                            </ModalActions>
                        </form>
                    </ModalContent>
                </ModalOverlay>
            )}
        </PageContainer>
    );
};

export default DistributorIntegrations;

const Subtitle = styled.p`
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const TypeBreakdown = styled.div`
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.textSecondary || '#666'};
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 2fr 1fr;
    gap: 1.5rem;

    @media (max-width: 1200px) {
        grid-template-columns: 1fr;
    }
`;

const TableCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    overflow: hidden;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th, td {
        padding: 0.75rem 1rem;
        text-align: left;
    }

    thead {
        background: rgba(130, 22, 146, 0.05);
    }

    tbody tr {
        cursor: pointer;
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }

    tbody tr.active {
        background: rgba(130, 22, 146, 0.08);
    }
`;

const DetailCard = styled.div`
    border: 1px solid rgba(130, 22, 146, 0.15);
    border-radius: 12px;
    padding: 1rem;
    min-height: 280px;
`;

const DetailContent = styled.div`
    h4 {
        margin-top: 0;
    }
`;

const DetailList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;

    li {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
    }

    strong {
        color: ${({ theme }) => theme.colors.textSecondary || '#666'};
    }
`;

const Placeholder = styled.div`
    text-align: center;
    padding: 2.5rem;
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    padding: 0.25rem 0.75rem;
    border-radius: 999px;
    font-size: 0.875rem;
    text-transform: capitalize;
    background: ${({ $status }) => ($status === 'healthy' ? 'rgba(0, 200, 83, 0.15)' : $status === 'warning' ? 'rgba(255, 193, 7, 0.2)' : 'rgba(244, 67, 54, 0.15)')};
    color: ${({ $status }) => ($status === 'healthy' ? 'rgba(0, 150, 136, 1)' : $status === 'warning' ? '#b26a00' : '#d32f2f')};
`;

const WebhookBadge = styled(StatusBadge)`
    background: ${({ $status }) => ($status === 'healthy' ? 'rgba(139, 195, 74, 0.15)' : $status === 'stale' ? 'rgba(255, 167, 38, 0.2)' : 'rgba(229, 57, 53, 0.15)')};
`;

const IconButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: rgba(130, 22, 146, 1);
    padding: 0.25rem;
`;

const PrimaryButton = styled.button`
    background: rgba(130, 22, 146, 1);
    color: #fff;
    border: none;
    padding: 0.625rem 1.125rem;
    border-radius: 999px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
`;

const SecondaryButton = styled.button`
    background: transparent;
    color: rgba(130, 22, 146, 1);
    border: 1px solid rgba(130, 22, 146, 0.4);
    padding: 0.625rem 1.125rem;
    border-radius: 999px;
    cursor: pointer;
`;

const ModalOverlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 999;
`;

const ModalContent = styled.div`
    background: #fff;
    border-radius: 12px;
    max-width: 480px;
    width: 100%;
    padding: 1.5rem;

    form {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;
    }
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;

    button {
        border: none;
        background: none;
        font-size: 1.4rem;
        cursor: pointer;
    }
`;

const CheckboxRow = styled.label`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
`;

const ModalActions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 0.5rem;
`;
